import React, { useState } from "react";

import { css } from "@emotion/css";
import { CellContext } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { NurseInclusionPatient } from "@/models/NurseInclusionPatientModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type ActionsColumnProps = CellContext<NurseInclusionPatient, unknown>;

export const ActionsColumn: React.FC<ActionsColumnProps> = ({ row }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutate: cancelSuggestion, isPending } = Queries.practitioner.useCancelNurseInclusionSuggestion();

  if (row.original.status !== "suggested_to_doc") {
    return null;
  }

  const onClose = () => setConfirmOpen(false);

  return (
    <>
      <Button data-action="inclusion.nurse-demand.cancel" variant="outlined" onClick={() => setConfirmOpen(true)}>
        {t("nurseInclusion.history.cancelDemand")}
      </Button>
      <Dialog
        id={`inclusion-nurse-demand-cancel-dialog-${row.original.id}`}
        open={confirmOpen}
        onClose={onClose}
        maxWidth="sm"
      >
        <Card>
          <CardHeader
            id={`inclusion-nurse-demand-cancel-confirm-${row.original.id}`}
            title={t("nurseInclusion.history.cancelDemandTitle")}
            variant="contained"
            onExit={onClose}
          />
          <CardContent className={styles.content}>
            <Typography variant="body">{t("nurseInclusion.history.cancelDemandDescription")}</Typography>
          </CardContent>
          <CardFooter>
            <Button data-action="inclusion.nurse-demand.dismiss" variant="outlined" onClick={onClose}>
              {t("button.cancel")}
            </Button>
            <Button
              data-action="inclusion.nurse-demand.accept"
              variant="contained"
              onClick={() =>
                cancelSuggestion(row.original.id, {
                  onSuccess: () => setConfirmOpen(false),
                })
              }
              pending={isPending}
            >
              {t("button.validate")}
            </Button>
          </CardFooter>
        </Card>
      </Dialog>
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css({
    padding: theme.spacing(10, 12),
    textAlign: "center",
  }),
});
